import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { TranslatableEditor } from '../../../Translations/Inputs';
import { getServerErrors } from '../../../../common/helpers';
import { replacementKeys } from './EditEventConfirmationMessagesForm';
import { translationsForAttribute } from '../../../Translations/helpers';
import MailEditorLayout from '../../../Common/MailEditorLayout';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useValidation from '../../../../common/useValidation';

export interface EditEventWaitlistFormProps {
  event: Event;
}

const EditEventWaitlistForm = ({ event }: EditEventWaitlistFormProps) => {
  const { t } = useTranslation();

  const getValues = (event: Event) => ({
    enabled: event.enable_waitlist,
    confirmation_mail_text: event.waitlist_confirmation_mail_text,
    confirmation_mail_text_translations: translationsForAttribute(
      event.translations,
      'waitlist_confirmation_mail_text',
    ),
  });

  const form = useForm({
    values: getValues(event),
  });

  const editEventWaitlistSchema = yup.object().shape({
    enable_waitlist: yup.boolean(),
  });

  useNavigationPrompt(form.changed());

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      waitlist: {
        ...form.values,
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
      setFormKey((key) => key + 1);
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventWaitlistSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'capacity'),
  });

  return (
    <UI.Form onSubmit={() => editEvent()} key={formKey}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} prefix="capacity" />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('waitlist')}
          </UI.Legend>
          <UI.InputDescription>
            {t('waitlist_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup sc={{ valid: !errors.enabled, touched: form.touched.enabled }}>
          <UI.Checkbox
            {...form.checkbox('enabled')}
            id="Enabled"
            sc={{ box: true, block: true }}
          >
            <UI.Strong sc={{ block: true }}>
              {t('enable_waitlist_label')}
            </UI.Strong>
            {t('enable_waitlist_description')}
          </UI.Checkbox>
        </UI.InputGroup>

        <UI.InputGroup
          sc={{ valid: !errors.confirmation_mail_text, touched: form.touched.confirmation_mail_text }}
        >
          <UI.InputLabel id="WaitlistConfirmationMailTextLabel">
            {t('waitlist_confirmation_mail_text')}
          </UI.InputLabel>
          <MailEditorLayout
            editor={(props) => (
              <TranslatableEditor
                form={form}
                name="confirmation_mail_text"
                title={t('waitlist_confirmation_mail_text')}
                defaultLocale={event.locale}
                options="full"
                variables={replacementKeys}
                attributes={{
                  'aria-labelledby': 'WaitlistConfirmationMailTextLabel',
                }}
                {...props}
              />
            )}
            value={form.values.confirmation_mail_text}
            event={event}
            showRegistrationSummary
          />
          <UI.ErrorMessages
            attribute={t('waitlist_confirmation_mail_text')}
            errors={errors.confirmation_mail_text}
          />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventWaitlistForm;
